import { colorConstants } from 'config/colorConstants';
import dayjs from 'dayjs';
import { FieldsOnCorrectTypeRule } from 'graphql';
import _ from 'lodash';
import { fetchCurrency, formatNumWithKMBAlt, formatString } from 'utils/helperMethods';

//bump chart parser
export const bumpReportChartParser = (
    data: any,
    from: 'increments' | 'promo' | 'groupBy' = 'increments',
    y2AxisLabel: null | string,
    config: any,
    appConfig: any,
) => {
    const fieldKeyLabelMapping: any = config.fieldKeyLabelMapping;
    const groupByKeysMapping: any[] = config.stackedColors;
    const bumpChartY2Colors: any[] = config.bumpChartY2Colors;
    let result: any = {
        legend: [],
        abs: {
            data: [],
            minVal: 0,
            maxVal: 0,
            lineMinVal: Number.MAX_SAFE_INTEGER,
            lineMaxVal: 0,
            extras: {},
            lineFields: [],
            lineColors: [],
        },
        percentage: {
            data: [],
            minVal: 0,
            maxVal: 100,
            lineMinVal: Number.MAX_SAFE_INTEGER,
            lineMaxVal: 0,
            extras: {},
            lineFields: [],
            lineColors: [],
        },
    };
    const labelConvertedToArray = y2AxisLabel?.split(',');
    const legendItems =
        labelConvertedToArray?.map((item: any, index: number) => {
            const color = bumpChartY2Colors[index % bumpChartY2Colors?.length];
            return { label: item, color: color, style: { height: 2 } };
        }) || [];
    result.abs.lineColors = [...legendItems];
    result.percentage.lineColors = [...legendItems];

    const legendKeyMapping: any = {
        increments: [],
        promo: [],
    };
    const { barChartData, y2Data } = data;
    let extraObj: any = {};
    //for tooltip
    if ('extraFields' in barChartData) {
        barChartData.extraFields?.trends?.forEach((item: any) => {
            const formattedDate = dayjs(item.end_timestamp).format(appConfig?.commonConfig?.dateFormatAlt);
            extraObj[formattedDate] = {};
            item?.values?.forEach((item: any, index: number) => {
                extraObj[formattedDate][barChartData.extraFields?.fields?.[index]] = item;
            });
        });
    }
    result.abs.extras = extraObj;
    result.percentage.extras = extraObj;
    const chartFields: any[] = barChartData.fields;
    const chartData: any[] = barChartData.trends;
    //for groupBy
    try {
        //if function called from groupBy dynamically create legends
        if (from === 'groupBy') {
            chartFields.forEach((item, idx) => {
                const label = item;
                const color = groupByKeysMapping[idx] || colorConstants.brown;
                result.legend.push({ label: formatString(label, 26), color, fullLabel: label });
                chartData.forEach((period, index) => {
                    const dateString = dayjs(period.end_timestamp).format(appConfig?.commonConfig?.dateFormatAlt);
                    const totalSales =
                        period.values?.reduce(
                            (accumulator: any, currentValue: any) => Number(accumulator) + Number(currentValue),
                        ) || 0;
                    result.abs.minVal = Math.min(result.abs.minVal, totalSales, Number(period.values[idx]));
                    result.abs.maxVal = Math.max(result.abs.maxVal, totalSales);
                    result.percentage.minVal = Math.min(
                        result.percentage.minVal,
                        period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                    );
                    const timePeriodObjAbs = {
                        sales: Number(period.values[idx]),
                        type: label,
                        date: dateString,
                        color,
                        value: null,
                        totalSales: idx === 0 ? totalSales : undefined,
                        index: index,
                    };
                    const timePeriodObjPercentage = {
                        ...timePeriodObjAbs,
                        sales: period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                        totalSales: undefined,
                    };
                    result.percentage.data.push(timePeriodObjPercentage);
                    result.abs.data.push(timePeriodObjAbs);
                    extraObj = {
                        ...extraObj,
                        [dateString]: {
                            ...(extraObj?.[dateString] || {}),
                            'Total Sales': totalSales,
                        },
                    };
                });
            });
            //FOR INCREMENTS AND PROMO
            //Y2 AXIS EXISTS ONLY FOR INCREMENTS AND PROMO
        } else {
            //arranging y2 data for kpis selected
            const y2Result: any[] = [];
            legendKeyMapping?.[from]?.push({ label: 'YA Sales', color: '#d0d0d7' });

            y2Data?.[0]?.trends?.forEach((trend: any, trendIndex: number) => {
                const temp: any = {};
                y2Data?.forEach((data: any) => {
                    const roundedValue = _.round(data?.trends?.[trendIndex]?.values?.[0], 2);
                    result.abs.lineMinVal = Math.min(result.abs.lineMinVal, roundedValue);
                    result.abs.lineMaxVal = Math.max(result.abs.lineMaxVal, roundedValue);
                    result.percentage.lineMinVal = Math.min(
                        result.percentage.lineMinVal,
                        data?.trends?.[trendIndex]?.values?.[0],
                    );
                    result.percentage.lineMaxVal = Math.max(
                        result.percentage.lineMaxVal,
                        data?.trends?.[trendIndex]?.values?.[0],
                    );
                    temp[data?.fields?.[0]] = roundedValue;
                });
                y2Result.push(temp);
            });
            const y2KeysData = Object.keys(y2Result?.[0] || {});
            chartFields.forEach((item, idx) => {
                const label = fieldKeyLabelMapping[item]['label'];
                const color = fieldKeyLabelMapping[item]['color'];
                const legendItem = { label, color };
                legendKeyMapping?.[from]?.push(legendItem);
                chartData.forEach((period, index) => {
                    const upliftKey = 'promo_lift' in period ? 'promo_lift' : 'media_lift';
                    const dateString = dayjs(period.end_timestamp).format(appConfig?.commonConfig?.dateFormatAlt);
                    const overlaySales = period?.overlay_sales;
                    const uplift = period?.[upliftKey];
                    const totalSales =
                        period.values?.reduce((accumulator: any, currentValue: any, index: number) => {
                            return Number(accumulator) + Number(currentValue);
                        }) || 0;

                    // add uplift for increments
                    extraObj = {
                        ...extraObj,
                        [dateString]: {
                            ...(extraObj?.[dateString] || {}),
                            ...(from === 'increments'
                                ? {
                                      [upliftKey]: config.isPromoUpliftPercentValue
                                          ? `${formatNumWithKMBAlt(uplift * 100, 2, 2)}%`
                                          : uplift,
                                  }
                                : {}),
                            'Total Sales': totalSales,
                        },
                    };

                    result.abs.minVal = Math.min(
                        result.abs.minVal,
                        totalSales,
                        Number(period.values[idx]),
                        Number(overlaySales),
                    );
                    result.abs.maxVal = Math.max(result.abs.maxVal, totalSales, Number(overlaySales));
                    result.percentage.minVal = Math.min(
                        result.percentage.minVal,
                        period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                    );
                    const timePeriodObjAbs = {
                        sales: Number(period.values[idx]),
                        type: label,
                        date: dateString,
                        color,
                        // value: idx === 0 ? y2ChartData[index].values[0] : null,
                        ...(idx === 0 ? y2Result?.[index] || {} : {}),
                        areaData: idx === 0 ? overlaySales : null,
                        totalSales: idx === 0 ? totalSales : undefined,
                        index: index,
                    };
                    const timePeriodObjPercentage = {
                        ...timePeriodObjAbs,
                        sales: period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                        totalSales: undefined,
                        areaData: null,
                    };
                    result.abs.data.push(timePeriodObjAbs);
                    result.percentage.data.push(timePeriodObjPercentage);
                });
            });
            result.abs.lineFields = y2KeysData;
            result.percentage.lineFields = y2KeysData;
            legendKeyMapping[from] = [...legendKeyMapping[from], ...legendItems];
            result.legend.push(...legendKeyMapping[from]);
        }
        result.abs.extras = extraObj;
        result.percentage.extras = extraObj;
        return result;
    } catch (error) {
        console.log(error, 'logging error');
        throw error;
    }
};

//promo comparison parser
export const bumpReportComparisonChartParser = (
    data: any,
    from: 'increments' | 'promo' | 'groupBy' = 'increments',
    y2AxisLabel: null | string,
    config: any,
    appConfig: any,
) => {
    const currency = fetchCurrency(appConfig);
    const fieldKeyLabelMapping: any = config.fieldKeyLabelMapping;
    const groupByKeysMapping: any[] = config.stackedColors;
    let result: any = {
        legend: [],
        abs: { data: [], minVal: 0, maxVal: 0, lineMinVal: 0, lineMaxVal: 0, extras: {} },
        percentage: { data: [], minVal: 0, maxVal: 100, lineMinVal: 0, lineMaxVal: 0, extras: {} },
    };
    const legendKeyMapping = {
        increments: [],
        promo: [],
    };
    const { barChartData } = data;
    let extraObj: any = {};
    //for tooltip
    if ('extraFields' in barChartData) {
        barChartData.extraFields?.trends?.forEach((item: any) => {
            const formattedDate = dayjs(item.end_timestamp).format(appConfig?.commonConfig?.dateFormatAlt);
            extraObj[formattedDate] = {};
            item?.values?.forEach((item: any, index: number) => {
                extraObj[formattedDate][barChartData.extraFields?.fields?.[index]] = item;
            });
        });
    }
    const chartFields: any[] = barChartData.fields;
    const chartData: any[] = barChartData.trends;
    let totalSales = 0;
    const tableData: any = {};
    let resultTableData: any[] = [];
    let extraTableData: any[] = [];
    let yaSalesData = {};
    let upliftLabel = '';
    //for groupBy
    try {
        //if function called from groupBy dynamically create legends
        if (from === 'groupBy') {
            chartFields.forEach((item, idx) => {
                const label = item;
                const color = groupByKeysMapping[idx] || colorConstants.brown;
                tableData[label] = {
                    label,
                    'Abs Sales': [currency, 0, ''],
                    '% Sales': ['', 0, '%'],
                };
                result.legend.push({ label: formatString(label, 26), color });
                let overallTotalSales = 0;
                chartData.forEach((period, index) => {
                    const dateString = dayjs(period.end_timestamp).format(appConfig?.commonConfig?.dateFormatAlt);

                    const totalSales =
                        period.values?.reduce(
                            (accumulator: any, currentValue: any) => Number(accumulator) + Number(currentValue),
                        ) || 0;
                    result.abs.minVal = Math.min(result.abs.minVal, totalSales, Number(period.values[idx]));
                    result.abs.maxVal = Math.max(result.abs.maxVal, totalSales);
                    result.percentage.minVal = Math.min(
                        result.percentage.minVal,
                        period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                    );
                    const timePeriodObjAbs = {
                        sales: Number(period.values[idx]),
                        type: label,
                        date: dateString,
                        color,
                        value: null,
                        totalSales: idx === 0 ? totalSales : undefined,

                        index: index,
                    };

                    extraObj = {
                        ...extraObj,
                        [dateString]: {
                            ...(extraObj?.[dateString] || {}),
                            'Total Sales ': totalSales,
                        },
                    };

                    //table abs sales
                    tableData[label]['Abs Sales'][1] += Number(period.values[idx]);
                    const timePeriodObjPercentage = {
                        ...timePeriodObjAbs,
                        sales: period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                        totalSales: undefined,
                    };
                    result.percentage.data.push(timePeriodObjPercentage);
                    result.abs.data.push(timePeriodObjAbs);
                });
                //updating table total sales
                totalSales += tableData[label]['Abs Sales'][1];
                overallTotalSales += totalSales;
                extraTableData = [
                    {
                        label: 'Total Sales',
                        'Abs Sales': [currency, overallTotalSales, ''],
                        '% Sales': ['', 100, '%'],
                    },
                ];
            });
            resultTableData.push(...extraTableData);
            chartFields.forEach((item, idx) => {
                //table percentage sales
                tableData[item]['% Sales'][1] = totalSales ? (tableData[item]['Abs Sales'][1] / totalSales) * 100 : 0;
                resultTableData.push(tableData[item]);
            });
            //FOR INCREMENTS AND PROMO
        } else {
            let avgUplift = 0;
            chartFields.forEach((item, idx) => {
                const label = fieldKeyLabelMapping[item]['label'];
                const color = fieldKeyLabelMapping[item]['color'];
                tableData[fieldKeyLabelMapping[item]['tableLabel']] = {
                    label: fieldKeyLabelMapping[item]['tableLabel'],
                    'Abs Sales': [currency, 0, ''],
                    '% Sales': ['', 0, '%'],
                };
                let overallTotalSales = 0;
                let totalYASales = 0;
                let totalUplift = 0;

                chartData.forEach((period, index) => {
                    const upliftKey = 'promo_lift' in period ? 'promo_lift' : 'media_lift';
                    const dateString = dayjs(period.end_timestamp).format(appConfig?.commonConfig?.dateFormatAlt);
                    const overlaySales = period?.overlay_sales;
                    const uplift = period?.[upliftKey];
                    totalUplift += uplift;
                    totalYASales += overlaySales;
                    upliftLabel = config.tooltipExtraItems[upliftKey].label;

                    const totalSales =
                        period.values?.reduce((accumulator: any, currentValue: any, index: number) => {
                            return Number(accumulator) + Number(currentValue);
                        }) || 0;

                    // only add uplift for increments
                    extraObj = {
                        ...extraObj,
                        [dateString]: {
                            ...(extraObj?.[dateString] || {}),
                            ...(from === 'increments'
                                ? {
                                      [upliftKey]: config.isPromoUpliftPercentValue
                                          ? `${formatNumWithKMBAlt(uplift * 100, 2, 2)}%`
                                          : uplift,
                                  }
                                : {}),
                            'Total Sales': totalSales,
                        },
                    };
                    result.abs.minVal = Math.min(
                        result.abs.minVal,
                        totalSales,
                        Number(period.values[idx]),
                        Number(overlaySales),
                    );
                    result.abs.maxVal = Math.max(result.abs.maxVal, totalSales, Number(overlaySales));
                    result.percentage.minVal = Math.min(
                        result.percentage.minVal,
                        period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                    );
                    const timePeriodObjAbs = {
                        sales: Number(period.values[idx]),
                        type: label,
                        date: dateString,
                        color,
                        value: null,
                        areaData: idx === 0 ? overlaySales : null,
                        totalSales: idx === 0 ? totalSales : undefined,
                        index: index,
                    };
                    //table abs sales
                    tableData[fieldKeyLabelMapping[item]['tableLabel']]['Abs Sales'][1] += Number(period.values[idx]);
                    const timePeriodObjPercentage: any = {
                        ...timePeriodObjAbs,
                        sales: period.values[idx] ? (Number(period.values[idx]) / totalSales) * 100 : 0,
                        totalSales: undefined,
                        areaData: null,
                    };
                    result.abs.data.push(timePeriodObjAbs);
                    result.percentage.data.push(timePeriodObjPercentage);
                });
                //updating table total sales
                totalSales += tableData[fieldKeyLabelMapping[item]['tableLabel']]['Abs Sales'][1];
                overallTotalSales += totalSales;
                avgUplift = totalUplift / chartData.length;
                extraTableData = [
                    {
                        label: 'Total Sales',
                        'Abs Sales': [currency, overallTotalSales, ''],
                        '% Sales': ['', 100, '%'],
                    },
                ];
                yaSalesData = { label: 'YA Sales', 'Abs Sales': [currency, totalYASales, ''], '% Sales': '' };
            });

            chartFields.forEach((item, idx) => {
                //table percentage sales
                tableData[fieldKeyLabelMapping[item]['tableLabel']]['% Sales'][1] = totalSales
                    ? (tableData[fieldKeyLabelMapping[item]['tableLabel']]['Abs Sales'][1] / totalSales) * 100
                    : 0;
                resultTableData.push(tableData[fieldKeyLabelMapping[item]['tableLabel']]);
            });

            resultTableData = [...extraTableData, ...resultTableData, yaSalesData];
            // only add uplift for increments
            if (from === 'increments') {
                resultTableData.unshift({
                    label: upliftLabel,
                    'Abs Sales': ['', avgUplift, ''],
                    '% Sales': ['', avgUplift * 100, '%'],
                });
            }
            result.legend.push(...legendKeyMapping[from]);
        }
        result.abs.extras = extraObj;
        result.percentage.extras = extraObj;
        return { parsedData: result, tableData: resultTableData };
    } catch (error) {
        console.log(error, 'logging error');
        throw error;
    }
};
